import { FC, memo, useState } from "react";
import {
  Building,
  DotMark,
  Group,
  Pending,
  Time,
  UserAvatar,
} from "@carbon/icons-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GroupPathsEnum } from "@/features/groups/constants/group.paths";
import { IGroupPersonalRequest } from "@/features/groups/types/group.type";
import formatNumber from "@/utils/formatNumber";
import CancelModal from "./CancelModal";

interface GroupCardRequestProps {
  group: IGroupPersonalRequest;
}

const GroupCardRequest: FC<GroupCardRequestProps> = ({ group }) => {
  const [requesting] = useState(false);

  const isRootGroup = group.groupId === group.rootId;

  return (
    <div className="group relative bg-white rounded-2xl border border-secondary-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300 ease-out overflow-hidden">
      {/* Cover Image with Overlay */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
        {group.photoUrl ? (
          <img
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            src={group.photoUrl}
            alt={group.name}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50">
            <div className="text-center">
              {isRootGroup ? (
                <Building
                  size={48}
                  className="text-primary-400 mx-auto mb-2"
                />
              ) : (
                <Group
                  size={48}
                  className="text-primary-400 mx-auto mb-2"
                />
              )}
              <span className="text-sm font-medium text-primary-600">
                {isRootGroup ? "Tổ chức" : "Nhóm"}
              </span>
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>

        {/* Group Type Badge */}
        <div className="absolute top-4 left-4">
          <Badge
            className={`
            px-2 py-1 text-xs font-medium rounded-full border-0 backdrop-blur-sm
            ${
              isRootGroup
                ? "bg-white/90 text-primary-700"
                : "bg-white/90 text-secondary-700"
            }
          `}
          >
            {isRootGroup ? (
              <>
                <Building
                  size={12}
                  className="mr-1"
                />
                Tổ chức
              </>
            ) : (
              <>
                <Group
                  size={12}
                  className="mr-1"
                />
                Nhóm
              </>
            )}
          </Badge>
        </div>

        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <Badge className="px-2 py-1 text-xs font-medium rounded-full border-0 backdrop-blur-sm bg-amber-100/90 text-amber-700">
            <Pending
              size={12}
              className="mr-1"
            />
            Đang chờ
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header with Category */}
        <div className="mb-4">
          {/* Parent Organization */}
          {group.rootName && (
            <div className="flex items-center text-xs font-medium text-secondary-500 uppercase tracking-wider mb-1">
              <span>Tổ chức</span>
              <DotMark
                size={4}
                className="mx-2"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      className="text-primary-600 hover:text-primary-700 transition-colors duration-200 normal-case max-w-[180px] truncate"
                      to={GroupPathsEnum.GROUP_DETAIL.replace(
                        ":id",
                        String(group.rootId)
                      )}
                    >
                      {group.rootName}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="text-xs">{group.rootName}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Group Title */}
          <h3 className="mb-3">
            <Link
              className="text-xl font-bold text-secondary-900 hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-tight"
              to={GroupPathsEnum.GROUP_DETAIL.replace(
                ":id",
                String(group.groupId)
              )}
            >
              {group.name}
            </Link>
          </h3>
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-between mb-6">
          {/* Request Status Info */}
          <div className="flex items-center space-x-2 bg-amber-50 px-3 py-1.5 rounded-full">
            <Time
              size={14}
              className="text-amber-600"
            />
            <span className="text-xs font-medium text-amber-700">
              Yêu cầu đã gửi
            </span>
          </div>

          {/* Members Count */}
          <div className="flex items-center text-sm text-secondary-600 bg-secondary-50 px-3 py-1.5 rounded-full">
            <UserAvatar
              size={16}
              className="mr-2 text-secondary-500"
            />
            <span className="font-medium">
              {formatNumber(group.numberOfMembers)}
            </span>
            <span className="ml-1 text-secondary-500">thành viên</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <CancelModal
            inviteId={group.id}
            groupName={group.name}
            rootGroupId={group.rootId}
            groupLock={group.groupLock}
            groupId={group.id}
            button={
              <Button
                loading={requesting}
                type="button"
                variant="ghost"
                className="w-full  hover:text-red-700 hover:bg-red-50 border border-gray-200 hover:border-red-300 transition-all duration-200"
              >
                Hủy yêu cầu
              </Button>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default memo(GroupCardRequest);
